import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, SlidersHorizontal, X, Grid3X3, List } from 'lucide-react';
import { ProductCard } from '../../components/shared/ProductCard';
import { ProductGridSkeleton } from '../../components/shared/Skeletons';
import { EmptyState } from '../../components/shared/EmptyState';
import { productService } from '../../services/productService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { DeliveryNotice } from '../../components/shared/DeliveryNotice';

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const hasLoadedInitially = useRef(false);

  useEffect(() => {
    productService.getCategories()
      .then(setCategories)
      .catch((err) => {
        setError(err.message || 'Greška pri učitavanju kategorija');
        toast.error(err.message || 'Greška pri učitavanju kategorija');
      });
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedCategory) filters.categoryId = selectedCategory;
      if (search) filters.search = search;
      if (showOnlyAvailable) filters.available = true;
      const result = await productService.getProducts(filters);
      setProducts(result.data);
      setError('');
    } catch (err) {
      setProducts([]);
      setError(err.message || 'Greška pri učitavanju proizvoda');
      toast.error(err.message || 'Greška pri učitavanju proizvoda');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, search, showOnlyAvailable]);

  useEffect(() => {
    if (!hasLoadedInitially.current) {
      hasLoadedInitially.current = true;
      loadProducts();
      return;
    }

    const timeout = setTimeout(loadProducts, 200);
    return () => clearTimeout(timeout);
  }, [loadProducts]);

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory(null);
    setShowOnlyAvailable(false);
  };

  const hasFilters = search || selectedCategory || showOnlyAvailable;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="divider-ornament mb-3">
          <span className="font-serif text-sm text-gold uppercase tracking-widest">Nasa ponuda</span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">
          Svi Sjenički Proizvodi
        </h1>
        <p className="text-muted-foreground font-body-serif">
          Domaći, prirodni i autentični proizvodi direktno sa planine.
        </p>
      </div>

      <DeliveryNotice variant="full" />

      {/* Filters */}
      <div className="bg-card rounded-xl p-4 md:p-5 shadow-card border border-border mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pretrazi proizvode..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 font-ui bg-muted/50 border-input focus:border-primary"
            />
          </div>

          {/* Available toggle */}
          <button
            onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-ui font-medium transition-colors ${
              showOnlyAvailable
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background border-border text-muted-foreground hover:border-primary hover:text-primary'
            }`}
          >
            Samo dostupno
          </button>

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground font-ui"
            >
              <X size={14} className="mr-1" />
              Obrisi filtere
            </Button>
          )}
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-ui font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              Sve kategorije
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-ui font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results info */}
      {error && !loading && (
        <div className="mb-4 rounded-lg border border-border bg-card p-3">
          <p className="text-sm text-muted-foreground font-ui">{error}</p>
        </div>
      )}
      {!loading && (
        <p className="text-sm text-muted-foreground font-ui mb-4">
          {products.length} {products.length === 1 ? 'proizvod' : 'proizvoda'} pronađeno
        </p>
      )}

      {/* Product grid */}
      {loading ? (
        <ProductGridSkeleton count={8} />
      ) : products.length === 0 ? (
        <EmptyState
          title="Nema proizvoda"
          description="Promenite filtere ili pokusajte sa drugim pojmom."
        />
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
        >
          {products.map((product, i) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.35 }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
